import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus, Plus, X } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const PACKAGES = [
  {
    id: "start",
    name: "Start",
    price: 49,
    best: "For newly self-employed tradespeople.",
    cta: "Get Started",
    features: [
      "Simple professional logo",
      "Brand colours and fonts",
      "Domain name",
      "One-page website",
      "Mobile-friendly design",
      "Services and areas covered",
      "Contact & quote-request form",
      "Local SEO setup",
      "Customer management",
      "Quotes and invoices",
      "Hosting, SSL and maintenance",
      "One small website update each month",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 69,
    best: "For growing trade businesses.",
    popular: true,
    cta: "Choose Pro",
    features: [
      "Professional logo and brand identity",
      "Domain name",
      "Up to five website pages",
      "Individual service sections",
      "Project gallery",
      "Customer reviews",
      "Contact & quote-request forms",
      "Google Business Profile assistance",
      "Local SEO setup",
      "Customer and enquiry management",
      "Quotes and invoices",
      "Convert quotes into invoices",
      "Invoice payment tracking",
      "Automatic invoice reminders",
      "Hosting, SSL and maintenance",
      "Two small website updates each month",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    price: 99,
    best: "For established trade businesses.",
    cta: "Grow My Business",
    features: [
      "Everything in Pro",
      "Premium brand identity",
      "Up to ten website pages",
      "Dedicated service pages",
      "Up to three targeted location pages",
      "Before-and-after gallery",
      "Review-request system",
      "Quote follow-up reminders",
      "Website analytics",
      "Google Search Console setup",
      "Technical SEO monitoring",
      "Monthly performance report",
      "Priority support",
      "Up to one hour of website changes each month",
    ],
  },
];

const COMPARE = [
  { row: "Website pages", start: "1", pro: "Up to 5", growth: "Up to 10" },
  { row: "Location pages", start: "—", pro: "—", growth: "Up to 3" },
  { row: "Brand identity", start: "Essentials", pro: "Full identity", growth: "Premium" },
  { row: "Domain + hosting", start: true, pro: true, growth: true },
  { row: "Local SEO setup", start: true, pro: true, growth: true },
  { row: "Google Business Profile", start: "—", pro: true, growth: true },
  { row: "Google Search Console", start: "—", pro: "—", growth: true },
  { row: "Website analytics", start: "—", pro: "—", growth: true },
  { row: "Quotes and invoices", start: true, pro: true, growth: true },
  { row: "Convert quote → invoice", start: "—", pro: true, growth: true },
  { row: "Payment tracking", start: "—", pro: true, growth: true },
  { row: "Automatic reminders", start: "—", pro: true, growth: true },
  { row: "Quote follow-up", start: "—", pro: "—", growth: true },
  { row: "Review-request system", start: "—", pro: "—", growth: true },
  { row: "Monthly updates included", start: "1 small", pro: "2 small", growth: "Up to 1hr" },
  { row: "Priority support", start: "—", pro: "—", growth: true },
];

const FAQS = [
  { q: "Is there a setup fee?", a: "No. Every plan is one flat monthly payment — no build fee, no upfront design fee, no domain fee." },
  { q: "Is there a minimum contract?", a: "Yes — a 12-month minimum term. After that it rolls monthly with 30 days' notice to cancel." },
  { q: "Can I upgrade my package later?", a: "Yes. You can move up a tier at any time and only pay the difference from that month onwards." },
  { q: "Who owns the domain?", a: "You do. The domain is registered in your business's name and stays with you if you ever leave." },
];

const CompareCell = ({ v }) => {
  if (v === true) return <Check size={16} strokeWidth={2.2} className="mx-auto text-[color:var(--accent)]" />;
  if (v === "—") return <X size={14} strokeWidth={1.5} className="mx-auto text-[color:var(--soft)]" />;
  return <span className="text-[13px] text-[color:var(--ink)]">{v}</span>;
};

const PricingPage = () => {
  const [expanded, setExpanded] = useState({});
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="min-h-screen bg-[color:var(--bg)] overflow-x-hidden" data-testid="pricing-page">
      <SiteHeader />

      {/* Header */}
      <section className="pt-28 sm:pt-32 lg:pt-40 pb-12 lg:pb-20 border-b border-[color:var(--border-soft)]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-6 items-baseline mb-8">
            <div className="col-span-6"><span className="grid-ref">§ Pricing / Monthly</span></div>
            <div className="col-span-6 flex justify-end"><span className="stamp stamp-ink">No setup fee · 12-month term</span></div>
          </div>
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-7">
              <h1 className="h-xl text-[color:var(--ink)]">
                One monthly payment.<br />
                <span className="font-serif-italic">No large upfront cost.</span>
              </h1>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <p className="copy-lg text-[color:var(--muted)] max-w-md">
                Every plan includes design, build, hosting, maintenance and small ongoing updates. Chosen by newly self-employed tradespeople right through to established teams.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cards */}
      <section className="py-16 lg:py-24 border-b border-[color:var(--border-soft)]" data-testid="pricing-cards-section">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {PACKAGES.map((pkg, i) => {
              const isOpen = expanded[pkg.id];
              const shown = isOpen ? pkg.features : pkg.features.slice(0, 6);
              return (
                <article
                  key={pkg.id}
                  className={`relative rounded-sm border p-6 lg:p-8 flex flex-col ${
                    pkg.popular
                      ? "border-[color:var(--ink)] bg-[color:var(--ink)] text-white"
                      : "border-[color:var(--border)] bg-[color:var(--bg-paper)] text-[color:var(--ink)]"
                  }`}
                  data-testid={`pricing-card-${pkg.id}`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className={`grid-ref ${pkg.popular ? "text-white/60" : ""}`}>0{i + 1} / Plan</span>
                    {pkg.popular ? (
                      <span className="stamp stamp-accent">Most Popular</span>
                    ) : (
                      <span className="stamp stamp-ink">Available</span>
                    )}
                  </div>

                  <p className="font-display leading-none" style={{ fontSize: 'clamp(1.75rem, 1.5vw + 1rem, 2.25rem)' }}>{pkg.name}</p>
                  <p className={`text-[14px] mt-2 leading-[1.5] ${pkg.popular ? "text-white/70" : "text-[color:var(--muted)]"}`}>{pkg.best}</p>

                  <div className={`h-px my-6 ${pkg.popular ? "bg-white/15" : "bg-[color:var(--border-soft)]"}`} />

                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="font-display leading-none tabular" style={{ fontSize: 'clamp(3rem, 3vw + 2rem, 4rem)' }}>£{pkg.price}</span>
                    <span className={`text-[14px] ${pkg.popular ? "text-white/70" : "text-[color:var(--muted)]"}`}>/ month</span>
                  </div>

                  <ul className="space-y-2.5 flex-1">
                    {shown.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[14px] leading-snug">
                        <Check size={14} strokeWidth={2} className={`mt-0.5 flex-shrink-0 ${pkg.popular ? "text-[color:var(--accent)]" : "text-[color:var(--ink)]"}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {pkg.features.length > 6 && (
                    <button
                      onClick={() => setExpanded((p) => ({ ...p, [pkg.id]: !p[pkg.id] }))}
                      className={`mt-4 text-[12px] font-semibold uppercase tracking-widest inline-flex items-center gap-1.5 ${
                        pkg.popular ? "text-white/80 hover:text-white" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                      }`}
                      data-testid={`pricing-expand-${pkg.id}`}
                    >
                      {isOpen ? <>Show less <Minus size={12} /></> : <>Full feature list <Plus size={12} /></>}
                    </button>
                  )}

                  <Link
                    to={`/get-started?package=${pkg.id}`}
                    className={`mt-6 ${pkg.popular ? "btn-primary" : "btn-navy"} w-full`}
                    data-testid={`pricing-cta-${pkg.id}`}
                  >
                    {pkg.cta} <ArrowRight size={14} />
                  </Link>

                  <p className={`mt-4 text-[11px] ${pkg.popular ? "text-white/50" : "text-[color:var(--muted)]"}`}>
                    No setup fee. 12-month minimum term.
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16 lg:py-24 border-b border-[color:var(--border-soft)]" data-testid="pricing-compare-section">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-6 items-end mb-10">
            <div className="col-span-12 lg:col-span-7">
              <span className="grid-ref">§ Compare / Features</span>
              <h2 className="h-lg text-[color:var(--ink)] mt-3">
                Every feature, <span className="font-serif-italic">side by side</span>.
              </h2>
            </div>
          </div>

          <div className="overflow-x-auto -mx-6 lg:mx-0 px-6 lg:px-0">
            <table className="w-full min-w-[720px] text-left" data-testid="compare-table">
              <thead>
                <tr className="border-b border-[color:var(--border)]">
                  <th className="py-4 pr-4 text-[13px] font-semibold text-[color:var(--muted)] uppercase tracking-widest w-2/5">Feature</th>
                  <th className="py-4 px-3 text-center">
                    <span className="grid-ref">01</span>
                    <p className="font-display text-[18px] mt-1">Start</p>
                    <p className="text-[12px] text-[color:var(--muted)]">£49</p>
                  </th>
                  <th className="py-4 px-3 text-center bg-[color:var(--ink)] text-white rounded-t-sm">
                    <span className="grid-ref text-white/70">02</span>
                    <p className="font-display text-[18px] mt-1">Pro</p>
                    <p className="text-[12px] text-white/70">£69 · Most Popular</p>
                  </th>
                  <th className="py-4 px-3 text-center">
                    <span className="grid-ref">03</span>
                    <p className="font-display text-[18px] mt-1">Growth</p>
                    <p className="text-[12px] text-[color:var(--muted)]">£99</p>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((r, i) => (
                  <tr key={r.row} className={i % 2 === 0 ? "bg-[color:var(--bg-paper)]" : ""}>
                    <td className="py-3 pr-4 text-[14px] text-[color:var(--ink)]">{r.row}</td>
                    <td className="py-3 px-3 text-center"><CompareCell v={r.start} /></td>
                    <td className="py-3 px-3 text-center bg-[color:var(--ink)] text-white"><CompareCell v={r.pro} /></td>
                    <td className="py-3 px-3 text-center"><CompareCell v={r.growth} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ short */}
      <section className="py-16 lg:py-24 border-b border-[color:var(--border-soft)]" data-testid="pricing-faq">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8 lg:gap-14">
            <div className="col-span-12 lg:col-span-4">
              <span className="grid-ref">§ FAQ</span>
              <h2 className="h-lg text-[color:var(--ink)] mt-3">
                A few reasonable <span className="font-serif-italic">questions</span>.
              </h2>
              <a href="mailto:contact@readytrades.co.uk" className="btn-secondary mt-8 inline-flex" data-testid="pricing-faq-email">
                Email a question
              </a>
            </div>
            <div className="col-span-12 lg:col-span-8">
              <div className="border-t border-[color:var(--border)]">
                {FAQS.map((f, i) => {
                  const open = openFaq === i;
                  return (
                    <div key={f.q} className="border-b border-[color:var(--border-soft)]">
                      <button
                        onClick={() => setOpenFaq(open ? -1 : i)}
                        className="w-full flex items-start justify-between gap-6 py-5 text-left"
                      >
                        <div className="flex items-start gap-4">
                          <span className="grid-ref pt-1">0{i + 1}</span>
                          <span className="font-display text-[18px] lg:text-[22px] text-[color:var(--ink)] leading-tight">{f.q}</span>
                        </div>
                        <span className="flex-shrink-0 mt-1">{open ? <Minus size={16} /> : <Plus size={16} />}</span>
                      </button>
                      {open && (
                        <div className="pb-6 pl-12 pr-6 text-[15px] leading-[1.65] text-[color:var(--muted)]">{f.a}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="h-lg text-[color:var(--ink)] max-w-3xl mx-auto">
            Not sure which plan? <span className="font-serif-italic">We'll help you pick.</span>
          </h2>
          <p className="copy-lg text-[color:var(--muted)] mt-6 max-w-xl mx-auto">
            Book a quick call with Elliot — no obligation. We'll talk through your trade, your area and what would actually work for you.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/get-started" className="btn-primary" data-testid="pricing-final-cta">Get My Free Preview <ArrowRight size={15} /></Link>
            <a href="mailto:contact@readytrades.co.uk" className="btn-secondary">Talk to ReadyTrades</a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default PricingPage;
